import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { EmailConfigApiService } from '../services/email-config-api.service';
import {
  EmailProviderConfigResponse,
  EmailProviderConfigRequest,
  EmailProviderType
} from '../models/email-config.model';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';

@Component({
  selector: 'app-email-config-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    HasPermissionDirective
  ],
  templateUrl: './email-config-page.component.html'
})
export class EmailConfigPageComponent implements OnInit {
  loading = false;
  saving = false;
  testing = false;
  error = '';
  successMessage = '';
  showForm = false;
  config: EmailProviderConfigResponse | null = null;

  readonly providerOptions: { value: EmailProviderType; label: string }[] = [
    { value: 'SMTP', label: 'SMTP' },
    { value: 'RESEND', label: 'Resend' },
    { value: 'CUSTOM_API', label: 'Custom API' },
    { value: 'SENDGRID', label: 'SendGrid' },
    { value: 'MAILGUN', label: 'Mailgun' },
    { value: 'AWS_SES', label: 'Amazon SES' }
  ];

  formProviderType: EmailProviderType = 'SMTP';
  formFromEmail = '';
  formFromName = '';
  formSmtpHost = '';
  formSmtpPort: number = 587;
  formSmtpUsername = '';
  formSmtpPassword = '';
  formSmtpUseTls = true;
  formSmtpUseSsl = false;
  formApiKey = '';
  formApiDomain = '';
  formApiBaseUrl = '';

  isSuperAdmin = false;

  constructor(
    private emailConfigApi: EmailConfigApiService,
    private datetimeService: DatetimeService,
    private authService: AuthService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    const user = this.authService.getCurrentUser();
    this.isSuperAdmin = user?.roleName === 'Super Admin';
    if (!this.isSuperAdmin) return;
    this.cargarConfig();
  }

  cargarConfig(): void {
    this.loading = true;
    this.error = '';
    this.successMessage = '';

    this.emailConfigApi.getGlobalConfig().subscribe({
      next: (data) => {
        this.config = data;
        this.loading = false;
      },
      error: (err) => {
        if (err.status === 404) {
          this.config = null;
        } else {
          this.error = err.error?.message || 'Error al cargar la configuración global de correo';
        }
        this.loading = false;
      }
    });
  }

  abrirFormulario(): void {
    this.error = '';
    this.successMessage = '';

    if (this.config) {
      this.formProviderType = this.config.providerType;
      this.formFromEmail = this.config.fromEmail;
      this.formFromName = this.config.fromName ?? '';
      this.formSmtpHost = this.config.smtpHost ?? '';
      this.formSmtpPort = this.config.smtpPort ?? 587;
      this.formSmtpUsername = this.config.smtpUsername ?? '';
      this.formSmtpPassword = '';
      this.formSmtpUseTls = this.config.smtpUseTls ?? true;
      this.formSmtpUseSsl = this.config.smtpUseSsl ?? false;
      this.formApiKey = '';
      this.formApiDomain = this.config.apiDomain ?? '';
      this.formApiBaseUrl = this.config.apiBaseUrl ?? '';
    } else {
      this.resetForm();
    }

    this.showForm = true;
  }

  cancelar(): void {
    this.showForm = false;
    this.error = '';
    this.successMessage = '';
  }

  private resetForm(): void {
    this.formProviderType = 'SMTP';
    this.formFromEmail = '';
    this.formFromName = '';
    this.formSmtpHost = '';
    this.formSmtpPort = 587;
    this.formSmtpUsername = '';
    this.formSmtpPassword = '';
    this.formSmtpUseTls = true;
    this.formSmtpUseSsl = false;
    this.formApiKey = '';
    this.formApiDomain = '';
    this.formApiBaseUrl = '';
  }

  get isSmtp(): boolean {
    return this.formProviderType === 'SMTP';
  }

  get isSmtpConfig(): boolean {
    return this.config?.providerType === 'SMTP';
  }

  get showApiKey(): boolean {
    return !this.isSmtp;
  }

  get showApiDomain(): boolean {
    return this.formProviderType === 'MAILGUN';
  }

  get showApiBaseUrl(): boolean {
    return this.formProviderType === 'RESEND' || this.formProviderType === 'CUSTOM_API' || this.formProviderType === 'MAILGUN';
  }

  get isApiBaseUrlRequired(): boolean {
    return this.formProviderType === 'CUSTOM_API';
  }

  guardar(): void {
    const faltantes: string[] = [];
    if (!this.formProviderType) faltantes.push('Tipo de proveedor');
    if (!this.formFromEmail.trim()) faltantes.push('Correo remitente');

    if (faltantes.length > 0) {
      this.error = `Complete los campos requeridos: ${faltantes.join(', ')}.`;
      this.toast.warning({ title: 'Campos incompletos', message: this.error });
      return;
    }

    this.saving = true;
    this.error = '';
    this.successMessage = '';

    const request: EmailProviderConfigRequest = {
      providerType: this.formProviderType,
      fromEmail: this.formFromEmail.trim(),
      fromName: this.formFromName.trim() || undefined
    };

    if (this.formProviderType === 'SMTP') {
      request.smtpHost = this.formSmtpHost.trim() || undefined;
      request.smtpPort = this.formSmtpPort || undefined;
      request.smtpUsername = this.formSmtpUsername.trim() || undefined;
      request.smtpPassword = this.formSmtpPassword || undefined;
      request.smtpUseTls = this.formSmtpUseTls;
      request.smtpUseSsl = this.formSmtpUseSsl;
    } else {
      request.apiKey = this.formApiKey || undefined;
      request.apiDomain = this.formApiDomain.trim() || undefined;
      request.apiBaseUrl = this.formApiBaseUrl.trim() || undefined;
    }

    const operation$ = this.config
      ? this.emailConfigApi.updateGlobal(request)
      : this.emailConfigApi.createOrReplaceGlobal(request);

    operation$.subscribe({
      next: (data) => {
        this.config = data;
        this.saving = false;
        this.showForm = false;
        this.successMessage = 'Configuración global guardada correctamente';
      },
      error: (err) => {
        this.saving = false;
        this.error = err.error?.message || 'Error al guardar la configuración global';
      }
    });
  }

  enviarCorreoPrueba(toEmail?: string): void {
    this.testing = true;
    this.error = '';
    this.successMessage = '';

    this.emailConfigApi.sendTestEmail(toEmail).subscribe({
      next: () => {
        this.testing = false;
        this.successMessage = 'Correo de prueba enviado correctamente';
      },
      error: (err) => {
        this.testing = false;
        this.error = err.error?.message || 'Error al enviar el correo de prueba';
      }
    });
  }

  async desactivar(): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Desactivar configuración',
      message: '¿Está seguro de desactivar esta configuración global de correo?',
      confirmText: 'Desactivar'
    });
    if (!ok) return;

    this.emailConfigApi.deactivateGlobal().subscribe({
      next: () => {
        this.config = null;
        this.successMessage = 'Configuración global desactivada correctamente';
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al desactivar la configuración global';
      }
    });
  }

  formatDate(dateStr: string): string {
    return this.datetimeService.formatDateTimeNumeric(dateStr);
  }

  providerLabel(type: EmailProviderType): string {
    return this.providerOptions.find(o => o.value === type)?.label ?? type;
  }
}